import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../common/formkit/FormPageLayout";
import { Section, TextField, TextAreaField, CurrencyField, SegmentField } from "../../common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../common/formkit/formHelpers";
import { showToast } from "../../common/notification/Notification";
import {
  listKhatabookCustomers,
  addKhatabookCustomer,
  editKhatabookCustomer,
  searchCustomerTransactions,
  addCustomerTransaction,
  editCustomerTransaction,
} from "../../../api/tools/toolsApi";
import { khatabookListUrl, useToolsToken } from "./toolFormHelpers";
import { TX_MODES, customerName, customerPhone, dateInputValue, todayDate, toApiDateTime } from "./khatabookModel";

const CUSTOMER_FORM_ID = "khatabook-customer-form";
const TX_FORM_ID = "khatabook-transaction-form";

const customerSchema = Yup.object({
  name: Yup.string().trim().required("Please enter the customer name"),
  phone: Yup.string().trim().required("Please enter the mobile number"),
});

const TX_TYPES = [
  { value: "CREDIT", label: "Credit (Money In)" },
  { value: "DEBIT", label: "Debit (Money Out)" },
];

const errorText = (err, fallback) => err?.response?.data?.text || err?.response?.data?.message || err?.message || fallback;

const NotFound = ({ text, onBack }) => (
  <div className="fk-page" style={{ maxWidth: 1200 }}>
    <div className="alert alert-warning d-flex align-items-center justify-content-between" role="alert">
      <span>{text}</span>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onBack}>
        Back to Khatabook
      </button>
    </div>
  </div>
);

/** Shared body: unsaved-changes guard + focus on first error around the given fields. */
const FormBody = ({ id, saved, children }) => {
  const { dirty } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);
  return (
    <Form id={id} noValidate>
      <FocusFirstError />
      {children}
    </Form>
  );
};

/* ---------------- Add / edit a customer ---------------- */

/** /partner/tools/khatabook/:bookId/customers/new and /…/customers/:customerId/edit */
export const KhatabookCustomerFormPage = () => {
  const { bookId, customerId } = useParams();
  const isEdit = Boolean(customerId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = useToolsToken();
  const exit = useExitWizard(khatabookListUrl(bookId));

  const [customer, setCustomer] = useState(location.state?.customer || null);
  const [loading, setLoading] = useState(isEdit && !location.state?.customer);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || customer || !token) return undefined;
    let cancelled = false;
    listKhatabookCustomers(token, bookId, 1, 1000)
      .then((data) => {
        if (cancelled) return;
        const list = data?.khataBookCustomers || [];
        const found = list.find((c) => String(c.id) === String(customerId));
        if (found) setCustomer(found);
        else setNotFound(true);
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isEdit, customer, token, bookId, customerId]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = { name: values.name, phone: values.phone };
      const res = isEdit
        ? await editKhatabookCustomer(token, customer?.id ?? customerId, body)
        : await addKhatabookCustomer(token, bookId, body);
      // The API answers 200 with statusType FAILURE for business errors
      if (res?.statusType && res.statusType !== "SUCCESS") throw new Error(res.text || "Failed to save customer");
      showToast(res?.text || (isEdit ? "Customer updated successfully" : "Customer added successfully"), { type: "success" });
      setSaved(true);
      allowLeaveOnce();
      setTimeout(() => navigate(khatabookListUrl(bookId), { replace: true }), 0);
    } catch (err) {
      // Form data stays as entered
      showToast(errorText(err, "Failed to save customer"), { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound text="This customer could not be found. It may have been removed." onBack={() => navigate(khatabookListUrl(bookId))} />;

  return (
    <FormPageLayout
      title={isEdit ? "Edit Customer" : "Add Customer to Khatabook"}
      subtitle={isEdit ? "Update this customer's details." : "Add a customer to start recording credit and debit entries."}
      formId={CUSTOMER_FORM_ID}
      submitLabel={isEdit ? "Update" : "Add Customer"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik
        initialValues={{ name: customerName(customer), phone: customerPhone(customer) }}
        validationSchema={customerSchema}
        onSubmit={handleSubmit}
      >
        <FormBody id={CUSTOMER_FORM_ID} saved={saved}>
          <Section title="Customer details">
            <div className="fk-grid">
              <TextField name="name" label="Customer Name" required placeholder="e.g. John Doe" />
              <TextField name="phone" label="Mobile Number" required type="tel" placeholder="e.g. 9876543210" />
            </div>
          </Section>
        </FormBody>
      </Formik>
    </FormPageLayout>
  );
};

/* ---------------- Record / edit a transaction ---------------- */

/** /partner/tools/khatabook/:bookId/customers/:customerId/transactions/new and /…/:txId/edit */
export const KhatabookTransactionFormPage = () => {
  const { bookId, customerId, txId } = useParams();
  const isEdit = Boolean(txId);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = useToolsToken();
  const backUrl = khatabookListUrl(bookId, customerId);
  const exit = useExitWizard(backUrl);

  const [tx, setTx] = useState(location.state?.transaction || null);
  const [loading, setLoading] = useState(isEdit && !location.state?.transaction);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || tx || !token) return undefined;
    let cancelled = false;
    searchCustomerTransactions(token, customerId, 1, 100)
      .then((data) => {
        if (cancelled) return;
        const list = data?.khataTransactions || [];
        const found = list.find((t) => String(t.id) === String(txId));
        if (found) setTx(found);
        else setNotFound(true);
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isEdit, tx, token, customerId, txId]);

  // Same rules as the mobile app: amount required, a new entry cannot be dated in the past
  const validationSchema = Yup.object({
    type: Yup.string().oneOf(["CREDIT", "DEBIT"]).required(),
    amount: Yup.number().typeError("Please enter a valid amount").moreThan(0, "Please enter a valid amount").required("Please enter a valid amount"),
    khataBookMode: Yup.string().required(),
    note: Yup.string(),
    transactionDate: Yup.string()
      .required("Date is required")
      .test("not-past", "Transaction date cannot be in the past", (value) => isEdit || !value || value >= todayDate()),
  });

  const initialType = isEdit
    ? String(tx?.type || "").toUpperCase() === "DEBIT"
      ? "DEBIT"
      : "CREDIT"
    : searchParams.get("type") === "DEBIT"
      ? "DEBIT"
      : "CREDIT";

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = {
        amount: Number(values.amount),
        type: values.type,
        note: values.note,
        transactionDate: toApiDateTime(values.transactionDate, tx?.transactionDate),
        khataBookMode: values.khataBookMode,
      };
      const res = isEdit
        ? await editCustomerTransaction(token, tx?.id ?? txId, body)
        : await addCustomerTransaction(token, customerId, body);
      if (res?.statusType && res.statusType !== "SUCCESS") throw new Error(res.text || "Failed to save transaction");
      showToast(res?.text || (isEdit ? "Transaction updated" : "Transaction recorded"), { type: "success" });
      setSaved(true);
      allowLeaveOnce();
      setTimeout(() => navigate(backUrl, { replace: true }), 0);
    } catch (err) {
      showToast(errorText(err, "Failed to save transaction"), { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound text="This transaction could not be found. It may have been removed." onBack={() => navigate(backUrl)} />;

  return (
    <FormPageLayout
      title={isEdit ? "Edit Transaction" : "Record Transaction"}
      subtitle={isEdit ? "Update this transaction." : "Record money in (credit) or money out (debit) for this customer."}
      formId={TX_FORM_ID}
      submitLabel={isEdit ? "Update Entry" : "Save Entry"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik
        initialValues={{
          type: initialType,
          amount: tx?.amount ?? "",
          khataBookMode: tx?.mode || "CASH",
          note: tx?.note || "",
          transactionDate: dateInputValue(tx?.transactionDate),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <FormBody id={TX_FORM_ID} saved={saved}>
          <Section title="Transaction details">
            <div className="fk-grid">
              <SegmentField name="type" label="Transaction Type" required options={TX_TYPES} className="fk-col-full" />
              <CurrencyField name="amount" label="Amount" required placeholder="e.g. 5,000" />
              <SegmentField name="khataBookMode" label="Payment Mode" required options={TX_MODES} />
              <TextField name="transactionDate" label="Date" required type="date" />
              <TextAreaField
                name="note"
                label="Note"
                rows={3}
                placeholder="e.g. Materials purchased, Partial payment, etc."
                className="fk-col-full"
              />
            </div>
          </Section>
        </FormBody>
      </Formik>
    </FormPageLayout>
  );
};
